import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Cookie, ShieldCheck, Sliders, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConsent } from '@/lib/consent';

/**
 * CookieBanner — first-visit cookie consent banner.
 *
 * - Renders only when consent has NOT been recorded yet.
 * - Bottom-left glassmorphism card with cyan/blue/violet edge glow.
 * - Three primary actions + Cookie Policy link.
 * - Animates in with framer-motion (respects prefers-reduced-motion).
 */
export default function CookieBanner() {
  const { decided, acceptAll, rejectNonEssential, openManager, managerOpen } = useConsent();
  const reduced = useReducedMotion();
  const visible = !decided && !managerOpen;

  const initial = reduced
    ? { opacity: 0 }
    : { opacity: 0, y: 24, scale: 0.98 };
  const animate = reduced
    ? { opacity: 1, transition: { duration: 0.18 } }
    : {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.36, ease: [0.22, 1, 0.36, 1] },
      };
  const exit = reduced
    ? { opacity: 0, transition: { duration: 0.18 } }
    : { opacity: 0, y: 12, transition: { duration: 0.2, ease: 'easeIn' } };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          key="cookie-banner"
          initial={initial}
          animate={animate}
          exit={exit}
          role="region"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
          className="
            fixed z-[1100]
            bottom-4 left-4 right-4
            sm:bottom-6 sm:left-6 sm:right-auto
            sm:max-w-[420px]
          "
          style={{ willChange: 'opacity, transform' }}
        >
          <div
            className="
              relative overflow-hidden rounded-2xl
              bg-gradient-to-b from-[#0B172A]/95 to-[#050B17]/95
              border border-[#7DDCFF]/25
              backdrop-blur-xl
              shadow-[0_24px_60px_rgba(0,0,0,0.55),0_0_42px_rgba(34,211,238,0.18)]
              p-5 sm:p-6
              text-white
            "
          >
            {/* Top edge gradient line */}
            <div
              aria-hidden="true"
              className="absolute top-0 left-0 right-0 h-px"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(34,211,238,0.65), rgba(124,58,237,0.65), transparent)',
              }}
            />
            {/* Soft corner glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-12 -left-10 h-40 w-40 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(34,211,238,0.18), transparent 70%)',
              }}
            />

            <div className="flex items-start gap-3">
              <span
                className="
                  inline-flex h-10 w-10 shrink-0 items-center justify-center
                  rounded-xl
                  bg-gradient-to-br from-[#22D3EE]/20 to-[#7C3AED]/20
                  border border-[#7DDCFF]/30
                  text-[#7DDCFF]
                  shadow-[0_0_18px_rgba(34,211,238,0.25)]
                "
                aria-hidden="true"
              >
                <Cookie className="h-5 w-5" />
              </span>

              <div className="flex-1 min-w-0">
                <h2
                  className="font-display text-[16px] sm:text-[17px] font-bold tracking-[-0.02em]"
                  data-testid="cookie-banner-title"
                >
                  Cookie Preferences
                </h2>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-300/90">
                  Ads Dashbound uses cookies to keep the site running, understand
                  how visitors use our pages, and improve advertiser onboarding.
                  You can accept all cookies, reject non-essential cookies, or
                  customize your preferences.
                </p>
                <p className="mt-2 text-[12px] text-slate-400">
                  <Link
                    to="/cookie-policy"
                    className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors"
                    data-testid="cookie-banner-policy-link"
                  >
                    Read the Cookie Policy
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <Button
                onClick={acceptAll}
                className="btn-primary-gradient text-white rounded-xl px-4 py-2 h-10 text-[13.5px] font-semibold border-0 sm:flex-1"
                data-testid="cookie-banner-accept-all"
              >
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Accept All
              </Button>
              <Button
                onClick={rejectNonEssential}
                variant="outline"
                className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-4 py-2 h-10 text-[13.5px] font-semibold transition-colors backdrop-blur-md sm:flex-1"
                data-testid="cookie-banner-reject"
              >
                <X className="h-4 w-4 mr-1.5" /> Reject Non-Essential
              </Button>
              <Button
                onClick={openManager}
                variant="ghost"
                className="text-slate-200 hover:text-white hover:bg-white/10 rounded-xl px-4 py-2 h-10 text-[13.5px] font-semibold transition-colors sm:flex-1"
                data-testid="cookie-banner-customize"
              >
                <Sliders className="h-4 w-4 mr-1.5" /> Customize
              </Button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
